import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ButterflyCursor from "../components/cosmic/ButterflyCursor";
import {
  ArrowLeft,
  ArrowRight,
  Phone,
  MapPin,
  Mail,
  Clock,
  Heart,
  HandHeart,
  Home as HomeIcon,
  Users,
  Utensils,
  Activity,
  Sparkles,
  HardHat,
  Menu,
  X,
} from "lucide-react";
import { useReveal } from "../lib/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRUSTEE_BABULAL =
  "https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/110rs9xo_WhatsApp%20Image%202026-04-25%20at%2012.01.01%20AM.jpeg";
const TRUSTEE_JITENDRA =
  "https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/7lpthebd_WhatsApp%20Image%202026-04-25%20at%2012.00.29%20AM.jpeg";

const constructionImages = [
  {
    src: "https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/g0quaycn_WhatsApp%20Image%202026-04-25%20at%2010.52.11%20AM%20%281%29.jpeg",
    label: "Main residential block",
    phase: "Phase 01 — Structure",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/638pslbw_WhatsApp%20Image%202026-04-25%20at%2010.52.11%20AM%20%282%29.jpeg",
    label: "Administration & community wing",
    phase: "Phase 02",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/1s3t68gp_WhatsApp%20Image%202026-04-25%20at%2010.52.12%20AM.jpeg",
    label: "Dormitory wing — exterior",
    phase: "Phase 02",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/h2vry34a_WhatsApp%20Image%202026-04-25%20at%2010.52.12%20AM%20%281%29.jpeg",
    label: "Wider campus view",
    phase: "Site",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/zz848vjw_WhatsApp%20Image%202026-04-25%20at%2010.52.11%20AM.jpeg",
    label: "Plastered facade nearing completion",
    phase: "Phase 03 — Plastering",
  },
];

const supporters = [
  {
    name: "Oswal Energies Limited",
    short: "Oswal Energies",
    logo: "https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/gi3wak7a_image.png",
    tag: "Energy & Infrastructure",
    url: "https://www.oswalenergies.com/",
  },
  {
    name: "Oswal Industries Limited",
    short: "Oswal Industries",
    logo: "https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/mp5aceoi_image.png",
    tag: "Industrial Manufacturing",
    url: "https://www.oswalvalves.com/",
  },
  {
    name: "Nimba Nature Cure Private Limited",
    short: "Nimba Nature Cure",
    logo: "https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/o4bz0pq9_image.png",
    tag: "Wellness & Healing",
    url: "https://www.nimba.in/",
  },
  {
    name: "Metal Forge India Private Limited",
    short: "Metal Forge India",
    logo: "https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/uw1h9pzu_image.png",
    tag: "Precision Forging",
    url: "https://metalforgeindia.com/",
  },
];

const services = [
  { icon: HomeIcon, title: "Permanent Shelter", desc: "A safe, dignified home for those abandoned by family or society. Clean rooms, warm beds and the simple right to be somewhere." },
  { icon: Activity, title: "Mental Health Care", desc: "Counselling, medication management and therapy for residents living with mental illness — supported by qualified professionals." },
  { icon: Utensils, title: "Daily Meals & Hygiene", desc: "Three home-cooked meals a day, fresh clothes, bathing and grooming care — restoring routine and self-respect." },
  { icon: Users, title: "Family Reunification", desc: "We trace and gently reach out to families. When possible, we reunite residents with loved ones who once let go." },
  { icon: HandHeart, title: "Companionship & Dignity", desc: "Volunteers, festivals, music, satsang — because no one should age or heal alone. Apna Ghar is, above all, a family." },
  { icon: Sparkles, title: "Skill & Spiritual Wellbeing", desc: "Light vocational activities, gardening, prayer and quiet — a slow path back to feeling alive and useful again." },
];

// ---------- Navbar ----------
function ApanGharNavbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#mission", label: "Mission" },
    { href: "#progress", label: "Progress" },
    { href: "#what-we-offer", label: "What We Offer" },
    { href: "#trustees", label: "Trustees" },
    { href: "#volunteer-ag", label: "Volunteer" },
    { href: "#contact-ag", label: "Contact" },
  ];

  return (
    <header
      data-testid="apanghar-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FAF4E8]/85 border-b border-[#E5D5BC]"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between">
        <Link to="/apan-ghar" data-testid="apanghar-logo" className="flex items-center gap-3">
          <span className="relative w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#5C3A1F" }}>
            <span className="absolute inset-0 rounded-full border border-[#C9A66B] scale-110 opacity-50" />
            <HomeIcon className="w-4 h-4 text-[#C9A66B]" strokeWidth={1.6} />
          </span>
          <span className="leading-tight">
            <span className="block font-display italic text-[#5C3A1F] text-xl">Apna Ghar</span>
            <span className="block text-[10px] tracking-[0.28em] uppercase text-[#8A6E50]">A Bokadia Trust Project</span>
          </span>
        </Link>

        <ul className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                data-testid={`apanghar-navlink-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm text-[#2D1810] hover:text-[#5C3A1F] transition-colors relative after:absolute after:bottom-[-6px] after:left-0 after:h-[1px] after:w-0 after:bg-[#C9A66B] hover:after:w-full after:transition-all"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/" data-testid="apanghar-back-home" className="text-sm text-[#5C3A1F] flex items-center gap-2 hover:text-[#C9A66B] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Hastkesar Panjrapole
          </Link>
        </div>

        <button
          data-testid="apanghar-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full border border-[#E5D5BC]"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-[#FAF4E8] border-t border-[#E5D5BC]">
          <ul className="px-6 py-6 space-y-4">
            {links.map((l) => (
              <li key={l.href}>
                <a href={l.href} onClick={() => setOpen(false)} className="block text-[#2D1810] text-base">
                  {l.label}
                </a>
              </li>
            ))}
            <li className="pt-2">
              <Link to="/" onClick={() => setOpen(false)} className="block text-center bg-[#5C3A1F] text-[#FAF4E8] px-5 py-3 rounded-full">
                ← Hastkesar Panjrapole
              </Link>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}

// ---------- Hero ----------
function ApanGharHero() {
  return (
    <section id="home" data-testid="apanghar-hero" className="relative min-h-[100svh] w-full overflow-hidden bg-[#FAF4E8]">
      <div className="absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full bg-[#5C3A1F] opacity-90" />
      <div className="absolute top-32 -right-20 w-72 h-72 rounded-full bg-[#C9A66B] opacity-30" />
      <div className="absolute -bottom-20 left-10 w-80 h-80 rounded-full bg-[#E8D5B7] opacity-60" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 min-h-[100svh] flex flex-col justify-center pt-24 pb-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-[1px] w-12 bg-[#5C3A1F]" />
              <span className="text-[#5C3A1F] text-xs tracking-[0.35em] uppercase font-semibold">
                A Bokadia Trust Project
              </span>
            </div>

            <div className="inline-flex items-center gap-3 mb-8 bg-[#5C3A1F] text-[#FAF4E8] rounded-full pl-3 pr-5 py-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#C9A66B] opacity-70 animate-ping" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#C9A66B]" />
              </span>
              <span className="text-[11px] tracking-[0.28em] uppercase font-semibold">
                Under Construction
              </span>
            </div>

            <h1 data-testid="apanghar-hero-title" className="font-display text-[#2D1810] text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1.02] tracking-tight">
              <span className="italic text-[#5C3A1F]">Apna</span> Ghar.
              <br />A home for those left behind.
            </h1>

            <p className="mt-8 text-[#5C4A3A] text-base sm:text-lg max-w-2xl leading-relaxed">
              For people living with mental illness. For those disowned by their
              families. For elders left at railway stations. Apna Ghar is being
              built to give them a clean bed, three meals, a doctor's care and —
              slowly — a family again.
            </p>

            <div className="mt-12 flex flex-wrap items-center gap-4">
              <a href="#volunteer-ag" data-testid="apanghar-volunteer-cta" className="group inline-flex items-center gap-3 bg-[#5C3A1F] hover:bg-[#4A2C16] text-[#FAF4E8] px-7 py-4 rounded-full text-sm font-semibold tracking-wide transition-all">
                Volunteer With Us
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a href="#progress" data-testid="apanghar-progress-cta" className="inline-flex items-center gap-3 border border-[#5C3A1F]/30 hover:bg-[#5C3A1F]/5 text-[#5C3A1F] px-7 py-4 rounded-full text-sm font-medium transition-all">
                <HardHat className="w-4 h-4" /> See Construction Progress
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="relative aspect-[4/5] rounded-3xl bg-[#5C3A1F] overflow-hidden p-10 flex flex-col justify-end">
                <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full border border-[#C9A66B]/30" />
                <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full border border-[#C9A66B]/40" />
                <div className="absolute top-6 right-6 w-14 h-14 rounded-full bg-[#C9A66B] flex items-center justify-center shadow-lg">
                  <Heart className="w-6 h-6 text-[#5C3A1F]" strokeWidth={1.5} />
                </div>
                <div className="relative">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#C9A66B] mb-4">Sanskrit</div>
                  <p className="font-display italic text-[#FAF4E8] text-3xl md:text-4xl leading-snug">
                    "अतिथि देवो भव।"
                  </p>
                  <p className="mt-3 text-[#FAF4E8]/75 text-sm">
                    The guest is god. Even when nobody else remembers them.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Mission ----------
function ApanGharMission() {
  const ref = useReveal();
  return (
    <section id="mission" data-testid="apanghar-mission" className="py-24 md:py-32 bg-[#F0E6D2]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div ref={ref} className="reveal grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-[1px] w-10 bg-[#5C3A1F]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#5C4A3A] font-semibold">Our Mission</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2D1810] leading-[1.05]">
              Where forgetting <span className="italic text-[#5C3A1F]">stops being a sentence</span>.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[#5C4A3A] text-lg leading-relaxed">
            <p>
              Apna Ghar exists for the people the world tries hardest not to
              see — the wandering, the unwell, the elderly left at temples and
              railway platforms, the chronically ill nobody comes back for.
            </p>
            <p>
              Run by the Bokadia family — the same hands that built Hastkesar
              Panjrapole — Apna Ghar will offer a permanent shelter, structured
              mental-health care, daily meals, hygiene, festivals, and most of
              all, the steady warmth of being known by name again.
            </p>
            <p>
              Every resident is a <span className="text-[#5C3A1F] font-medium">guest, not a patient</span>.
              A son or a sister or a grandfather waiting to be remembered.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Construction Progress ----------
function ApanGharConstruction() {
  const ref = useReveal();
  return (
    <section id="progress" data-testid="apanghar-construction" className="py-24 md:py-32 bg-[#FAF4E8]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div ref={ref} className="reveal grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-[1px] w-10 bg-[#5C3A1F]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#5C4A3A] font-semibold">Building, in progress</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2D1810] leading-[1.05]">
              The walls are rising. <span className="italic text-[#5C3A1F]">So is hope</span>.
            </h2>
            <p className="mt-6 text-[#5C4A3A] text-lg leading-relaxed max-w-2xl">
              Apna Ghar is being built brick by brick on land donated by the
              Bokadia family. Every photo below is a step closer to the day
              the first resident walks through the door.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#FAF4E8] border border-[#E5D5BC] rounded-2xl p-6 md:p-7 relative overflow-hidden">
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full border border-[#C9A66B]/40" />
              <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full border border-[#C9A66B]/20" />
              <div className="relative flex items-start gap-4">
                <div className="shrink-0 w-12 h-12 rounded-full bg-[#5C3A1F] flex items-center justify-center">
                  <HardHat className="w-5 h-5 text-[#C9A66B]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#C9A66B] font-semibold">Status</div>
                  <p className="mt-2 font-display italic text-[#2D1810] text-xl leading-snug">
                    Mid-construction. Phase 03 underway.
                  </p>
                  <p className="mt-2 text-[#5C4A3A] text-sm">
                    Help us bring this home alive — sponsor a window, a room,
                    or a year of meals.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-5">
          <ConstructionTile img={constructionImages[0]} className="col-span-12 md:col-span-8 row-span-2 min-h-[320px] md:min-h-[480px]" big />
          <ConstructionTile img={constructionImages[1]} className="col-span-12 md:col-span-4 min-h-[230px]" />
          <ConstructionTile img={constructionImages[2]} className="col-span-12 md:col-span-4 min-h-[230px]" />
          <ConstructionTile img={constructionImages[3]} className="col-span-6 md:col-span-6 min-h-[230px]" />
          <ConstructionTile img={constructionImages[4]} className="col-span-6 md:col-span-6 min-h-[230px]" />
        </div>
      </div>
    </section>
  );
}

function ConstructionTile({ img, className, big }) {
  return (
    <figure className={`relative rounded-2xl overflow-hidden group ${className}`}>
      <img
        src={img.src}
        alt={img.label}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#2D1810]/80 via-transparent to-transparent" />
      <figcaption className="absolute left-5 bottom-5 right-5">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#C9A66B]">{img.phase}</div>
        <div className={`font-display text-[#FAF4E8] mt-1 ${big ? "text-2xl md:text-3xl" : "text-lg"}`}>
          {img.label}
        </div>
      </figcaption>
    </figure>
  );
}

// ---------- What we offer ----------
function ApanGharOfferings() {
  const headRef = useReveal();
  return (
    <section id="what-we-offer" data-testid="apanghar-offerings" className="py-24 md:py-32 bg-[#F0E6D2]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div ref={headRef} className="reveal max-w-3xl mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-[1px] w-10 bg-[#5C3A1F]" />
            <span className="text-xs tracking-[0.3em] uppercase text-[#5C4A3A] font-semibold">What we offer</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2D1810] leading-[1.05]">
            Care that <span className="italic text-[#5C3A1F]">restores dignity</span>, not just shelter.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <article
                key={s.title}
                data-testid={`apanghar-offering-${i}`}
                className="group p-8 md:p-10 rounded-2xl bg-[#FAF4E8] border border-[#E5D5BC] hover:-translate-y-1 transition-all duration-500 hover:shadow-[0_20px_40px_-20px_rgba(92,58,31,0.3)]"
              >
                <div className="w-12 h-12 rounded-full bg-[#5C3A1F] flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5 text-[#C9A66B]" strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-2xl text-[#2D1810] leading-tight">{s.title}</h3>
                <p className="mt-3 text-[#5C4A3A] leading-relaxed text-[15px]">{s.desc}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------- Trustees ----------
function ApanGharTrustees() {
  const ref = useReveal();
  return (
    <section id="trustees" data-testid="apanghar-trustees" className="py-24 md:py-32 bg-[#FAF4E8]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div ref={ref} className="reveal max-w-3xl mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-[1px] w-10 bg-[#5C3A1F]" />
            <span className="text-xs tracking-[0.3em] uppercase text-[#5C4A3A] font-semibold">Our Trustees</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2D1810] leading-[1.05]">
            The same family. The same <span className="italic text-[#5C3A1F]">promise</span>.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <TrusteeCard
            photo={TRUSTEE_BABULAL}
            role="Founder & Trustee"
            name="Shri Babulal Hastimalji Bokadia"
            text={
              <>
                Founded with a heartfelt mission to care for the most forgotten,
                <span className="text-[#5C3A1F] font-medium"> Apna Ghar </span>
                stands today as a symbol of compassion. The vision of trustee
                <span className="text-[#5C3A1F] font-medium"> Shri Babulal Hastimalji Bokadia </span>
                continues to shape its journey — honouring the family's enduring tradition of
                kindness towards every living being.
              </>
            }
          />
          <TrusteeCard
            photo={TRUSTEE_JITENDRA}
            role="Co-Trustee"
            name="Shri Jitendra Hastimalji Bokadia"
            text={
              <>
                Supporting the vision alongside his elder brother,
                <span className="text-[#5C3A1F] font-medium"> Shri Jitendra Hastimalji Bokadia </span>
                plays a key role in the growth of Apna Ghar. His dedication and
                hands-on involvement ensure that the values of compassion and
                service continue to thrive across generations.
              </>
            }
          />
        </div>
      </div>
    </section>
  );
}

function TrusteeCard({ photo, role, name, text }) {
  return (
    <div className="relative bg-[#F0E6D2] border border-[#E5D5BC] rounded-2xl p-8 overflow-hidden">
      <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full border border-[#C9A66B]/40 pointer-events-none" />
      <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full border border-[#C9A66B]/20 pointer-events-none" />
      <div className="flex items-center gap-4">
        <div className="shrink-0 w-16 h-16 rounded-full overflow-hidden ring-2 ring-[#C9A66B]/40">
          <img src={photo} alt={name} className="w-full h-full object-cover" />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#C9A66B] font-semibold">{role}</div>
          <h3 className="mt-1 font-display text-xl md:text-2xl text-[#2D1810] leading-tight">{name}</h3>
        </div>
      </div>
      <p className="mt-6 text-[#5C4A3A] leading-relaxed text-[15px]">{text}</p>
    </div>
  );
}

// ---------- Volunteer ----------
function ApanGharVolunteer() {
  const interests = [
    "Companionship & Visits",
    "Mental Health / Therapy",
    "Cooking & Meals",
    "Festival Celebrations",
    "Family Tracing",
    "Fundraising & Awareness",
  ];
  const [form, setForm] = useState({ name: "", phone: "", email: "", interest: interests[0], message: "" });
  const [submitting, setSubmitting] = useState(false);
  const ref = useReveal();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.interest) {
      toast.error("Please fill your name, phone and area of interest");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/volunteer`, {
        name: form.name,
        phone: form.phone,
        email: form.email || undefined,
        interest: `Apna Ghar — ${form.interest}`,
        message: form.message,
      });
      toast.success("Thank you for offering your time", {
        description: "Our Apna Ghar team will reach out within 48 hours.",
      });
      setForm({ name: "", phone: "", email: "", interest: interests[0], message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="volunteer-ag" data-testid="apanghar-volunteer" className="py-24 md:py-32 bg-[#F0E6D2]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div ref={ref} className="reveal grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-[1px] w-10 bg-[#5C3A1F]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#5C4A3A] font-semibold">Volunteer</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2D1810] leading-[1.05]">
              Sit awhile. <br />
              <span className="italic text-[#5C3A1F]">Listen.</span> Be the family.
            </h2>
            <p className="mt-6 text-[#5C4A3A] leading-relaxed">
              Spend an afternoon sharing tea and stories. Lead a satsang.
              Cook a meal. Help us trace a missing family. The smallest hours
              you give become the rest of someone's life.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-3">
              {interests.map((i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-[#2D1810]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A66B]" /> {i}
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="lg:col-span-7 bg-[#FAF4E8] border border-[#E5D5BC] rounded-3xl p-8 md:p-12">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name" required>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="apanghar-volunteer-name" className="mt-2 w-full bg-[#FAF4E8] border border-[#E5D5BC] rounded-full px-5 py-3 outline-none focus:border-[#5C3A1F]" placeholder="Your full name" />
              </Field>
              <Field label="Phone" required>
                <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="apanghar-volunteer-phone" className="mt-2 w-full bg-[#FAF4E8] border border-[#E5D5BC] rounded-full px-5 py-3 outline-none focus:border-[#5C3A1F]" placeholder="+91…" />
              </Field>
              <Field label="Email (optional)" full>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="apanghar-volunteer-email" className="mt-2 w-full bg-[#FAF4E8] border border-[#E5D5BC] rounded-full px-5 py-3 outline-none focus:border-[#5C3A1F]" placeholder="you@example.com" />
              </Field>
              <Field label="Area of interest" full>
                <select value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} data-testid="apanghar-volunteer-interest" className="mt-2 w-full bg-[#FAF4E8] border border-[#E5D5BC] rounded-full px-5 py-3 outline-none focus:border-[#5C3A1F]">
                  {interests.map((i) => (<option key={i}>{i}</option>))}
                </select>
              </Field>
              <Field label="A note for our team (optional)" full>
                <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="apanghar-volunteer-message" className="mt-2 w-full bg-[#FAF4E8] border border-[#E5D5BC] rounded-2xl px-5 py-3 outline-none focus:border-[#5C3A1F] resize-none" placeholder="Tell us a little about yourself…" />
              </Field>
            </div>
            <button type="submit" disabled={submitting} data-testid="apanghar-volunteer-submit" className="mt-8 inline-flex items-center justify-center gap-3 bg-[#5C3A1F] hover:bg-[#4A2C16] disabled:opacity-60 text-[#FAF4E8] px-7 py-4 rounded-full font-medium transition-all">
              <HandHeart className="w-4 h-4 text-[#C9A66B]" />
              {submitting ? "Submitting…" : "Count me in"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-[11px] tracking-[0.22em] uppercase text-[#5C4A3A] font-semibold">
        {label} {required && <span className="text-[#C9A66B]">*</span>}
      </label>
      {children}
    </div>
  );
}

// ---------- Contact ----------
function ApanGharContact() {
  const ref = useReveal();
  return (
    <section id="contact-ag" data-testid="apanghar-contact" className="py-24 md:py-32 bg-[#FAF4E8]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div ref={ref} className="reveal grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-[1px] w-10 bg-[#5C3A1F]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#5C4A3A] font-semibold">Visit & contact</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2D1810] leading-[1.05]">
              Come in. The <span className="italic text-[#5C3A1F]">door is open</span>.
            </h2>
            <div className="mt-10 space-y-5">
              <ContactRow icon={MapPin} label="Address">
                Ahmedabad – Patan Highway Rd, nr. McCain Foods,<br />
                Expressway, Baliyasan, Mehsana, Gujarat – 384455
              </ContactRow>
              <ContactRow icon={Phone} label="Helpline (24/7)">
                <a href="tel:+918980152925" className="hover:text-[#5C3A1F]">+91 89801 52925</a>
              </ContactRow>
              <ContactRow icon={Mail} label="Email">
                <a href="mailto:hastkesarpanjrapole@gmail.com" className="hover:text-[#5C3A1F] break-all">
                  hastkesarpanjrapole@gmail.com
                </a>
              </ContactRow>
              <ContactRow icon={Clock} label="Visiting hours">
                Mon – Sun, 8:00 am – 6:00 pm
              </ContactRow>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl overflow-hidden border border-[#E5D5BC] aspect-[16/10]">
              <iframe
                title="Apna Ghar location"
                src="https://maps.google.com/maps?q=Baliyasan%2C%20Mehsana%2C%20Gujarat%20384455%20near%20McCain%20Foods&t=&z=14&ie=UTF8&iwloc=&output=embed"
                className="w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-full bg-[#FAF4E8] border border-[#E5D5BC] flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[#5C3A1F]" />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#8A6E50] font-semibold">{label}</div>
        <div className="mt-1 text-[#2D1810]">{children}</div>
      </div>
    </div>
  );
}

// ---------- Supporters ----------
function ApanGharSupporters() {
  const ref = useReveal();
  return (
    <section data-testid="apanghar-supporters" className="py-24 md:py-32 bg-[#F0E6D2] border-t border-[#E5D5BC]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div ref={ref} className="reveal text-center max-w-3xl mx-auto mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="h-[1px] w-10 bg-[#5C3A1F]" />
            <span className="text-xs tracking-[0.3em] uppercase text-[#5C4A3A] font-semibold">With gratitude</span>
            <span className="h-[1px] w-10 bg-[#5C3A1F]" />
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2D1810] leading-[1.05]">
            Standing with us, <span className="italic text-[#5C3A1F]">quietly and steadily</span>.
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {supporters.map((s, i) => (
            <a
              key={s.name}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`apanghar-supporter-${i}`}
              className="group relative bg-[#FAF4E8] border border-[#E5D5BC] rounded-2xl p-8 hover:-translate-y-1 transition-all duration-500 hover:shadow-[0_20px_40px_-20px_rgba(92,58,31,0.3)] overflow-hidden block"
            >
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full border border-[#C9A66B]/30 pointer-events-none" />
              <div className="relative flex items-center justify-center w-20 h-20 rounded-2xl bg-white border border-[#E5D5BC] p-3">
                <img src={s.logo} alt={`${s.short} logo`} className="w-full h-full object-contain" loading="lazy" />
              </div>
              <div className="mt-8">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#C9A66B] font-semibold">{s.tag}</div>
                <h3 className="mt-2 font-display text-xl md:text-[22px] text-[#2D1810] leading-snug">{s.name}</h3>
              </div>
              <div className="mt-6 h-[1px] w-full bg-[#E5D5BC]" />
              <div className="mt-4 text-[11px] tracking-[0.18em] uppercase text-[#8A6E50] flex items-center gap-1">
                Principal Supporter
                <span className="text-[#C9A66B] opacity-0 group-hover:opacity-100 transition-opacity">→</span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- Footer ----------
function ApanGharFooter() {
  return (
    <footer data-testid="apanghar-footer" className="bg-[#2D1810] text-[#FAF4E8]/80 pt-20 pb-10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid md:grid-cols-12 gap-10 pb-16 border-b border-[#FAF4E8]/10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full flex items-center justify-center bg-[#5C3A1F]">
                <HomeIcon className="w-4 h-4 text-[#C9A66B]" />
              </span>
              <div>
                <div className="font-display italic text-[#FAF4E8] text-xl">Apna Ghar</div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#C9A66B]">A Bokadia Trust Project</div>
              </div>
            </div>
            <p className="mt-6 leading-relaxed max-w-md">
              For the people the world tries hardest not to see — Apna Ghar is
              a permanent home, a hand to hold, and a name remembered.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C9A66B] mb-5">Explore</div>
            <ul className="space-y-3 text-sm">
              <li><a href="#mission" className="hover:text-[#C9A66B]">Mission</a></li>
              <li><a href="#progress" className="hover:text-[#C9A66B]">Progress</a></li>
              <li><a href="#what-we-offer" className="hover:text-[#C9A66B]">What We Offer</a></li>
              <li><a href="#trustees" className="hover:text-[#C9A66B]">Trustees</a></li>
              <li><a href="#volunteer-ag" className="hover:text-[#C9A66B]">Volunteer</a></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C9A66B] mb-5">Reach</div>
            <ul className="space-y-3 text-sm">
              <li className="break-all">hastkesarpanjrapole@gmail.com</li>
              <li>+91 89801 52925</li>
              <li>
                <Link to="/" className="hover:text-[#C9A66B] inline-flex items-center gap-2">
                  <ArrowLeft className="w-3.5 h-3.5" /> Hastkesar Panjrapole
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[#FAF4E8]/50">
          <div>© {new Date().getFullYear()} Apna Ghar · Run with love by the Bokadia Family.</div>
          <div>Made with compassion, in India.</div>
        </div>
      </div>
    </footer>
  );
}

// ---------- Page ----------
export default function ApanGhar() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="bg-[#FAF4E8] text-[#2D1810]">
      <ButterflyCursor />
      <ApanGharNavbar />
      <main>
        <ApanGharHero />
        <ApanGharMission />
        <ApanGharConstruction />
        <ApanGharOfferings />
        <ApanGharTrustees />
        <ApanGharVolunteer />
        <ApanGharContact />
        <ApanGharSupporters />
      </main>
      <ApanGharFooter />
    </div>
  );
}
